import { ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Typography,
} from '@mui/material';
import { useSearch } from '@tanstack/react-router';
import { addDays, getTime } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateObject } from 'react-multi-date-picker';
import { getTranslationBasedOnLanguage } from 'src/utils/get-translation-based-on-language';

import FilterGroup from '../../../../components/molecules/filter-group';
import { useSettings } from '../../../../contexts/settings-context';
import { X as XIcon } from '../../../../icons/x';

import DateRangeSelect from '@/components/molecules/date-range-select/date-range-select';
import { DatePeriod } from '@/components/molecules/date-range-select/types/date-period';
import { formatDateRangeValues } from '@/components/molecules/date-range-select/utils/format-date-range-dates';
import { useDestinations } from '@/features/destinations/hooks/query/use-destinations';
import { OrganizationLanguage } from '@/features/settings/api/types/gdpr';
import { VisitSource } from '@/features/visitors/api/types/visitor';
import { useCategories } from '@/features/visits/hooks/query/use-categories';
import { FilterOperator, FilterValue } from '@/hooks/meilisearch/types/filter-value';
import { FilterGroup as FilterGroupInterface } from '@/types/interfaces/filter-group';

type Anchor = 'top' | 'left' | 'bottom' | 'right';
type VisitHostConfirmedUiValue = 'true' | 'false' | 'null';

interface Props {
  onClose: () => void;
  open: boolean;
  filters: {
    [key: string]: {
      value: FilterValue;
      operator?: FilterOperator | undefined;
    };
  };
  handleChangeFilterValue: (
    facetType: string,
    val: string | string[] | number[],
    filterOperator?: FilterOperator | undefined,
  ) => void;
  anchor: Anchor;
  setState: (value: boolean) => void;
  resetFilters: () => void;
}

export const VisitorsFilters = ({
  open,
  onClose,
  handleChangeFilterValue,
  filters,
  resetFilters,
  setState,
  anchor,
}: Props) => {
  const { t, i18n } = useTranslation(['common', 'visits']);

  const { filter } = useSearch({ from: '/_layout/visits/' });

  const { settings } = useSettings();

  const toggleDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' ||
        (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }

    setState(open);
  };

  const [datesCreated, setDatesCreated] = useState<DateObject[]>([]);

  useEffect(() => {
    if (Array.isArray(filter?.visitCreatedAt?.value) && datesCreated.length === 0) {
      setDatesCreated(filter?.visitCreatedAt?.value?.map(val => new DateObject(val as string)));
    }
  }, [filters?.visitCreatedAt]);

  useEffect(() => {
    if (datesCreated?.length > 1) {
      const formattedDates = formatDateRangeValues(datesCreated, DatePeriod.DAY);

      handleChangeFilterValue(
        'visitCreatedAt',
        [getTime(formattedDates.from), getTime(formattedDates.to)],
        'timerange',
      );
    }
  }, [datesCreated]);

  const [datesExpected, setDatesExpected] = useState<DateObject[]>([]);

  useEffect(() => {
    if (Array.isArray(filter?.visitExpectedAt?.value) && datesExpected.length === 0) {
      setDatesExpected(filter?.visitExpectedAt?.value?.map(val => new DateObject(val as string)));
    }
  }, [filters?.visitExpectedAt]);

  useEffect(() => {
    if (datesExpected?.length > 1) {
      const formattedDates = formatDateRangeValues(datesExpected, DatePeriod.DAY);

      handleChangeFilterValue(
        'visitExpectedAt',
        [getTime(formattedDates.from), getTime(formattedDates.to)],
        'timerange',
      );
    }
  }, [datesExpected]);

  const [datesFinishes, setDatesFinishes] = useState<DateObject[]>([]);

  useEffect(() => {
    if (Array.isArray(filter?.visitFinishesAt?.value) && datesFinishes.length === 0) {
      setDatesFinishes(filter?.visitFinishesAt?.value?.map(val => new DateObject(val as string)));
    }
  }, [filters?.visitFinishesAt]);

  useEffect(() => {
    if (datesFinishes?.length > 1) {
      const formattedDates = formatDateRangeValues(datesFinishes, DatePeriod.DAY);

      handleChangeFilterValue(
        'visitFinishesAt',
        [getTime(formattedDates.from), getTime(formattedDates.to)],
        'timerange',
      );
    }
  }, [datesFinishes]);

  const { data: categoryData } = useCategories();

  // WE ARE FETCHING ONLY ONE DESTINATION HERE TO GET TOTAL AMOUNT OF DESTINATIONS
  const { data: destinationData } = useDestinations({ page: 1, perPage: 1 });

  const { data: destinationsData } = useDestinations({
    page: 1,
    perPage: destinationData?.total || 10,
    enabled: !!destinationData,
  });

  const filterGroups: FilterGroupInterface[] = [
    {
      id: 1,
      type: 'visitMainDestinationId',
      label: t('visits:filters.destination'),
      singleOption: false,
      items:
        destinationsData?.resources?.map(({ id, name }) => ({
          label: name,
          value: id.toString(),
        })) || [],
    },
    {
      id: 2,
      type: 'visitState',
      singleOption: false,
      label: t('visits:filters.visitState'),
      items: [
        { label: t('common:statusVariants.created'), value: 'created' },
        { label: t('common:statusVariants.cancelled'), value: 'cancelled' },
        { label: t('common:statusVariants.started'), value: 'started' },
        { label: t('common:statusVariants.finished'), value: 'finished' },
        { label: t('common:statusVariants.rejected'), value: 'rejected' },
      ],
    },
    {
      id: 3,
      type: 'isHandicapped',
      singleOption: false,
      label: t('visits:filters.isGuestHandicapped'),
      items: [
        { label: t('common:yes'), value: 'true' },
        { label: t('common:no'), value: 'false' },
      ],
    },
    {
      id: 4,
      type: 'hasAccessToParking',
      singleOption: false,
      label: t('visits:filters.hasAccessToParking'),
      items: [
        { label: t('common:yes'), value: 'true' },
        { label: t('common:no'), value: 'false' },
      ],
    },
    {
      id: 5,
      type: 'isPregnant',
      singleOption: false,
      label: t('visits:filters.isGuestPregnant'),
      items: [
        { label: t('common:yes'), value: 'true' },
        { label: t('common:no'), value: 'false' },
      ],
    },

    {
      id: 6,
      type: 'state',
      singleOption: false,
      label: t('visits:filters.visitorState'),
      items: [
        { label: t('common:statusVariantVisitor.invited'), value: 'invited' },
        { label: t('common:statusVariantVisitor.arrived'), value: 'arrived' },
        { label: t('common:statusVariantVisitor.cancelled'), value: 'cancelled' },
        { label: t('common:statusVariantVisitor.left'), value: 'left' },
      ],
    },
    {
      id: 7,
      type: 'visitCategory.id',
      singleOption: false,
      label: t('visits:filters.category'),
      items:
        categoryData?.map(category => ({
          label: getTranslationBasedOnLanguage(
            category.translations,
            i18n.language as OrganizationLanguage,
          ),
          value: category.id.toString(),
        })) || [],
    },
    {
      id: 8,
      type: 'visitSource',
      singleOption: false,
      label: t('visits:filters.source'),
      items: (['Host', 'Kiosk', 'Organization', 'Reception'] as VisitSource[]).map(source => ({
        label: t(`visits:source.${source}`),
        value: source,
      })),
    },
    {
      id: 9,
      type: 'visitHostConfirmed',
      singleOption: false,
      filterOperator: 'custom',
      label: t('visits:filters.requiresConfirmation'),
      items: [
        { label: t('common:yes'), value: 'false' },
        { label: t('common:no'), value: 'true' },
        {
          label: t('visits:filters.notRequired'),
          value: 'null',
        },
      ],
    },
  ];

  const onToggleFilterItemSelection = (value: string, group: FilterGroupInterface) => {
    const selectedFilterValues = (filters[group.type]?.value as string[] | undefined)?.map(value =>
      value?.toString(),
    );

    const initialSelectedCopy = selectedFilterValues ?? [];

    let selectedCopy = [...initialSelectedCopy];

    if (group.singleOption) {
      if (selectedCopy.includes(value)) {
        selectedCopy = [];
      } else {
        selectedCopy = [value];
      }
    } else {
      if (selectedCopy.includes(value)) {
        selectedCopy = selectedCopy.filter(item => item !== value);
      } else {
        selectedCopy.push(value);
      }
    }

    if (group.type === 'visitHostConfirmed') {
      handleChangeFilterValue(group.type, selectedCopy, group.filterOperator);
      return;
    }

    handleChangeFilterValue(group.type, selectedCopy, group.filterOperator);
  };

  return (
    <Drawer sx={{ height: '100%' }} anchor={anchor} open={open} onClose={toggleDrawer(false)}>
      <Box
        sx={{ width: anchor === 'top' || anchor === 'bottom' ? 'auto' : 380, overflowY: 'scroll' }}
        role="presentation">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            position: 'absolute',
            top: 0,
            width: '100%',
            backgroundColor: 'background.default',
            borderBottomWidth: '1px',
            borderBottomStyle: 'solid',
            borderBottomColor: settings.theme === 'light' ? '#e1e3ea' : '#2b2f3c',
            zIndex: 99999,
          }}>
          <Typography color="textPrimary" variant="subtitle1">
            {t('visits:filters.filters')}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <XIcon fontSize="small" sx={{ color: 'text.primary' }} />
          </IconButton>
        </Box>
        <Divider />
        <Box sx={{ minHeight: '100vh', pt: '61px', pb: '70px' }}>
          {filterGroups.map(group => (
            <Accordion key={group.type} disableGutters={true} sx={{ boxShadow: 'none' }}>
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls="panel1a-content"
                id={group.id.toString()}>
                <Typography variant="body2">{group.label}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <FilterGroup
                  items={group.items}
                  selected={((filters[group.type]?.value as string[]) ?? []).map(val =>
                    val.toString(),
                  )}
                  refine={value => {
                    onToggleFilterItemSelection(value, group);
                  }}
                />
              </AccordionDetails>
            </Accordion>
          ))}
          <Accordion key={'visitExpectedAt'} disableGutters={true} sx={{ boxShadow: 'none' }}>
            <AccordionSummary expandIcon={<ExpandMore />} aria-controls="panel1a-content" id="4">
              <Typography variant="body2">
                {t('visits:filters.expectedFrom')} - {t('visits:filters.to')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <DateRangeSelect
                maxDate={addDays(new Date(), 1)}
                value={datesExpected}
                onChange={setDatesExpected}
              />
            </AccordionDetails>
          </Accordion>
          <Accordion key={'visitFinishesAt'} disableGutters={true} sx={{ boxShadow: 'none' }}>
            <AccordionSummary expandIcon={<ExpandMore />} aria-controls="panel1a-content" id="4">
              <Typography variant="body2">
                {t('visits:filters.finishesFrom')} - {t('visits:filters.to')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <DateRangeSelect
                maxDate={addDays(new Date(), 1)}
                value={datesFinishes}
                onChange={setDatesFinishes}
              />
            </AccordionDetails>
          </Accordion>
          <Accordion key={'visitCreatedAt'} disableGutters={true} sx={{ boxShadow: 'none' }}>
            <AccordionSummary expandIcon={<ExpandMore />} aria-controls="panel1a-content" id="4">
              <Typography variant="body2">
                {t('visits:filters.createdFrom')} - {t('visits:filters.to')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <DateRangeSelect
                maxDate={addDays(new Date(), 1)}
                value={datesCreated}
                onChange={setDatesCreated}
              />
            </AccordionDetails>
          </Accordion>
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            position: 'absolute',
            bottom: 0,
            width: '100%',
            backgroundColor: 'background.default',
            borderTopWidth: '1px',
            borderTopStyle: 'solid',
            borderTopColor: settings.theme === 'light' ? '#e1e3ea' : '#2b2f3c',
          }}>
          <Button color="primary" onClick={resetFilters} variant="outlined">
            {t('common:button.reset')}
          </Button>
          <Button color="primary" onClick={onClose} variant="contained">
            Ok
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};
